import React from 'react';
import ReactECharts from 'echarts-for-react';
import { useTheme } from '@mui/material/styles';
import { useAtomValue } from 'jotai';
import { dashSummaryAtom } from '../atoms-types/dashSummary';

interface TempChartProps {
  minValue: number;
  maxValue: number;
  type: 'Temp' | 'Humidity';
}

const TempChart: React.FC<TempChartProps> = ({ minValue, maxValue, type }) => {
  const theme = useTheme();
  const dashSummary = useAtomValue(dashSummaryAtom);
  const currentValue = type === 'Temp' ? dashSummary.environSum.TempF : dashSummary.environSum.Humidity;
  const unit = type === 'Temp' ? '°F' : '%';
  const currentColor = type === 'Temp' ? theme.palette.success.main : theme.palette.info.main;
  
  const option = {
    grid: {
      left: '30%',
      right: '30%',
      top: '15%',
      bottom: '10%',
      containLabel: true
    },
    tooltip: {
      trigger: 'axis',
      appendToBody: true,
      formatter: function(params: any) {
        const currentValue = params.find((param: any) => param.seriesName === 'Current')?.value || 0;
        return `<span style="color:${currentColor}">●</span> Current: ${currentValue}${unit}<br/>` +
               `<span style="color:${theme.palette.error.main}">●</span> Max: ${maxValue}${unit}<br/>` +
               `<span style="color:${theme.palette.warning.main}">●</span> Min: ${minValue}${unit}<br/>` +
               `<span style="color:gray;font-size:12px">Min/Max over last 24hrs</span>`;
      }
    },
    xAxis: [
      {
        type: 'category',
        data: [`${currentValue}${unit}`],
        axisPointer: {
          type: 'shadow'
        },
        axisLabel: {
          color: theme.palette.text.primary,
          fontSize: 20,
          fontWeight: 'bold'
        },
        boundaryGap: false,
        axisTick: {
          show: false
        },
        axisLine: {
          show: false
        }
      }
    ],
    yAxis: [
      {
        type: 'value',
        name: type === 'Temp' ? 'Temperature' : 'Humidity',
        nameLocation: 'middle',
        nameGap: 50,
        nameTextStyle: {
          color: theme.palette.text.primary,
          fontSize: 20,
          fontWeight: 'bold'
        },
        min: Math.floor((Math.min(minValue, currentValue) - 5) / 5) * 5,
        max: Math.ceil((Math.max(maxValue, currentValue) + 5) / 5) * 5,
        interval: 5,
        splitLine: {
          show: true,
          interval: 5
        },
        axisLabel: {
          formatter: `{value} ${unit}`,
          color: theme.palette.text.secondary,
        }
      }
    ],
    series: [
      {
        name: 'Current',
        type: 'bar',
        tooltip: {
          valueFormatter: function (value: number) {
            return value + ` ${unit}`;
          }
        },
        data: [{
          value: currentValue,
          itemStyle: {
            color: currentColor,
          }
        }],
        barWidth: '50%',
        showBackground: true,
        backgroundStyle: {
          color: 'rgba(180, 180, 180, 0.2)'
        },
        z: 2
      },
      {
        name: 'Max',
        type: 'line',
        tooltip: {
          valueFormatter: function (value: number) {
            return value + ` ${unit}`;
          }
        },
        data: [],
        lineStyle: {
          color: theme.palette.error.main,
          width: 2,
          type: 'solid'
        },
        markLine: {
          silent: true,
          symbol: 'none',
          label: {
            show: true,
            formatter: 'Max',
            color: theme.palette.error.main,
            fontSize: 12,
            backgroundColor: 'transparent',
            borderColor: 'transparent'
          },
          data: [
            {
              yAxis: maxValue,
              lineStyle: {
                color: theme.palette.error.main,
                width: 2
              },
              value: maxValue
            }
          ]
        },
        z: 1
      },
      {
        name: 'Min',
        type: 'line',
        tooltip: {
          valueFormatter: function (value: number) {
            return value + ` ${unit}`;
          }
        },
        data: [],
        lineStyle: {
          color: theme.palette.warning.main,
          width: 2,
          type: 'solid'
        },
        markLine: {
          silent: true,
          symbol: 'none',
          label: {
            show: true,
            formatter: 'Min',
            color: theme.palette.warning.main,
            fontSize: 12,
            backgroundColor: 'transparent',
            borderColor: 'transparent'
          },
          data: [
            {
              yAxis: minValue,
              lineStyle: {
                color: theme.palette.warning.main,
                width: 2
              },
              value: minValue
            }
          ]
        },
        z: 1
      }
    ]
  };

  return (
    <ReactECharts 
      key={`${type}-${currentValue}`}
      option={option} 
      className="cancelSelectorName" 
      style={{ height: '300px', width: '100%' }} 
      opts={{ renderer: 'canvas' }}
      notMerge={false}
      lazyUpdate={true}
    />
  )
};

export default TempChart;
